import { Tooltip, Link, Checkbox } from "@mui/material";
import { round } from "lodash";
import {
  multiplierOptions,
  getFileNameFromUrl,
  multiplierMappings,
  prettyNumericRound,
  getFormattedNumber,
} from "../../utils";
import NumericLabel from "../../components/NumericLabel";
import { NOTE_TYPES } from "../../store/notesSlice";
import CustomEditComponent from "../DiscountAndPlanogram/DiscountTable/CustomEditComponent";
import config from "../../config";

const getPogPlanogramColumns = (
  processCallback,
  plannerNotes = [],
  retailerNotes = [],
  restrictEdit
) => [
  {
    field: "fl_dept_name",
    headerName: "Department",
    width: 160,
    sortable: false,
  },
  {
    field: "fl_class_name",
    headerName: "Category",
    width: 160,
    sortable: false,
  },
  {
    field: "fl_sub_name",
    headerName: "Planogram",
    width: 160,
    sortable: false,
  },
  {
    field: "region",
    headerName: "Region",
    width: 130,
    sortable: false,
    renderCell: (params) => {
      return (
        <Tooltip title={params.value ? params.value : ""}>
          <span>{params.value ? params.value : "-"}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "planogram",
    headerName: "Planogram #",
    width: 130,
    sortable: false,
    renderCell: (params) => (
      <Tooltip title={params.value ? getFileNameFromUrl(params.value) : ""}>
        <Link
          href={config.PogFileBasePath.concat(params.value)}
          target="_blank"
          rel="noreferrer noopener"
        >
          {getFileNameFromUrl(params.value)}
        </Link>
      </Tooltip>
    ),
  },
  {
    field: "core",
    headerName: "Core",
    width: 60,
    sortable: false,
    renderCell: (params) =>
      !params.value || params.value === "N" ? "No" : "Yes",
  },
  {
    field: "footage",
    headerName: "Footage",
    width: 80,
    sortable: false,
  },
  {
    field: "pogsku",
    headerName: "POG SKUs",
    width: 100,
    sortable: false,
    renderCell: (params) =>
      !!params?.value ? (
        <NumericLabel isSeparator value={round(params?.value, 2)} />
      ) : (
        ""
      ),
  },
  {
    field: "pogqty",
    headerName: "POG Qty",
    width: 80,
    sortable: false,
    renderCell: (params) => (
      <NumericLabel isSeparator value={round(params?.value || 0, 2)} />
    ),
  },
  {
    field: "pogcost",
    headerName: "POG Cost",
    width: 90,
    sortable: false,
    renderCell: (params) => {
      const value = prettyNumericRound(params.value || 0);
      return `$${getFormattedNumber(value)}`;
    },
  },
  {
    field: "missingskus",
    headerName: "Missing",
    width: 110,
    sortable: false,
    renderCell: (params) => (
      <NumericLabel isSeparator value={round(params?.value || 0, 2)} />
    ),
  },
  {
    field: "multiplier",
    headerName: "Multiplier",
    width: 85,
    sortable: false,
    renderCell: (params) => {
      return params?.value ? multiplierMappings[params.value] : "-";
    },
    renderEditCell: (params) => (
      <CustomEditComponent
        {...params}
        getProcessedRow={processCallback}
        fieldType="multiplier"
        default=""
        options={multiplierOptions}
      />
    ),
    editable: true,
  },
  {
    field: "adjqty",
    headerName: "Adj. Qty",
    width: 80,
    sortable: false,
    renderCell: (params) => (
      <NumericLabel isSeparator value={round(params.value || 0, 2)} />
    ),
  },
  {
    field: "adjcost",
    headerName: "Adj. Cost",
    width: 110,
    sortable: false,
    renderCell: (params) => {
      const value = prettyNumericRound(params.value || 0);
      return `$${getFormattedNumber(value)}`;
    },
  },
  {
    field: "totalCost",
    headerName: "Total Cost",
    width: 110,
    renderCell: (params) => {
      const value = prettyNumericRound(params.value || 0);
      return `$${getFormattedNumber(value)}`;
    },
  },
  {
    field: "PlannerAction",
    headerName: "Status",
    type: "boolean",
    width: 70,
    sortable: false,
    renderCell: (params) => {
      return (
        <Checkbox
          readOnly
          checked={Boolean(params.value)}
          disabled={!params.row.plannernotesid}
          sx={{ width: "auto", padding: "0rem" }}
        />
      );
    },
    renderEditCell: (params) => (
      <CustomEditComponent
        {...params}
        getProcessedRow={processCallback}
        fieldType="checkbox"
        disabled={!params.row.plannernotesid}
      />
    ),
    editable: true,
  },
  {
    field: "plannernotesid",
    headerName: "Planner Notes",
    width: 200,
    sortable: false,
    renderCell: (params) => {
      let currentValue = plannerNotes.find((note) => note?.id === params.value);
      currentValue = !!currentValue ? currentValue?.Notes : "";
      return (
        <Tooltip title={currentValue ? currentValue : ""}>
          <span className="truncated-notes">{currentValue}</span>
        </Tooltip>
      );
    },
    renderEditCell: (params) => (
      <CustomEditComponent
        {...params}
        getProcessedRow={processCallback}
        fieldType="autoComplete"
        noteType={NOTE_TYPES[0]}
        options={plannerNotes}
        restrictEdit={restrictEdit}
      />
    ),
    editable: true,
  },
  {
    field: "RetailerAction",
    headerName: "Status",
    type: "boolean",
    width: 70,
    sortable: false,
    renderCell: (params) => {
      return (
        <Checkbox
          readOnly
          checked={Boolean(params.value)}
          disabled={!params.row.retailernotesid}
          sx={{ width: "auto", padding: "0rem" }}
        />
      );
    },
    renderEditCell: (params) => (
      <CustomEditComponent
        {...params}
        getProcessedRow={processCallback}
        fieldType="checkbox"
        disabled={!params.row.retailernotesid}
      />
    ),
    editable: true,
  },
  {
    field: "retailernotesid",
    headerName: "Retailer Notes",
    width: 200,
    sortable: false,
    renderCell: (params) => {
      let currentValue = retailerNotes.find(
        (note) => note?.id === params.value
      );
      currentValue = !!currentValue ? currentValue?.Notes : "";
      return (
        <Tooltip title={currentValue ? currentValue : ""}>
          <span className="truncated-notes">{currentValue}</span>
        </Tooltip>
      );
    },
    renderEditCell: (params) => (
      <CustomEditComponent
        {...params}
        getProcessedRow={processCallback}
        fieldType="autoComplete"
        options={retailerNotes}
        noteType={NOTE_TYPES[1]}
        restrictEdit={restrictEdit}
      />
    ),
    editable: true,
  },
];

export const POG_POG_EDITABLE_FIELDS = getPogPlanogramColumns()
  .map((c) => c.editable && c.field)
  .filter((i) => !!i);

export default getPogPlanogramColumns;
